import { AxiosError } from 'axios';
import { createContext, FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import { IProgress, useGetProgress } from '@Domains/Progress';
import { useProfile } from '../UserProfileContext/UserProfileProvider';
interface IBreadcrumbs {
  title: string;
  startOperatingYear: string;
  endOperatingYear?: string;
  subtitle?: string;
  reportId?: string;
}

type ProgressContextProps = {
  progressData: IProgress | undefined;
  progressLoading: boolean;
  progressError: AxiosError | null;
  refetchProgressWithUpdate: (isUpdate: boolean) => void;
  isPublished: boolean;
  breadcrumbs: IBreadcrumbs;
  setBreadcrumbs: React.Dispatch<React.SetStateAction<IBreadcrumbs>>;
};

const ProgressContext = createContext<ProgressContextProps | undefined>(
  undefined
);

const ProgressBarProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { id } = useParams();
  const { user } = useProfile();
  const companyId = user?.I_COMPANY_ID?.toString() || '';

  const [
    { data: progressData, loading: progressLoading, error: progressError },
    refetchProgress,
  ] = useGetProgress(Number(id), companyId);

  const isPublished = progressData?.summarizeReport?.publishedDate
    ? true
    : false;

  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs>({
    title: '',
    startOperatingYear: '',
    endOperatingYear: '',
  });

  const refetchProgressWithUpdate = (isUpdate: boolean) => {
    const updateSummarizeFlag = isUpdate ? `&isUpdate=${isUpdate}` : '';

    refetchProgress({
      url: `/progress/${id}?companyId=${companyId}${updateSummarizeFlag}`,
    });
  };

  return (
    <ProgressContext.Provider
      value={{
        progressData,
        progressLoading,
        progressError,
        refetchProgressWithUpdate,
        isPublished,
        breadcrumbs,
        setBreadcrumbs,
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};

const useProgressBar = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export { ProgressBarProvider, useProgressBar };
